import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCw, Pause, Play, Trash2, Plane } from "lucide-react";

const SOURCE_COLORS = {
  "aerodatabox": "text-emerald-700 bg-emerald-50 border-emerald-200",
  "aerodatabox-webhook": "text-blue-700 bg-blue-50 border-blue-200",
  "swedavia": "text-amber-700 bg-amber-50 border-amber-200",
  "adsblol": "text-purple-700 bg-purple-50 border-purple-200",
  "opensky": "text-indigo-700 bg-indigo-50 border-indigo-200",
};

const STATUS_COLOR = (status) => {
  if (!status) return "text-gray-500";
  if (status === "ok") return "text-emerald-600";
  if (status.startsWith("http_4") || status.startsWith("http_5")) return "text-red-600";
  if (status === "error") return "text-red-600";
  return "text-amber-600";
};

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString("sv-SE", {
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return iso; }
}

export function FlightFetchLog() {
  const [items, setItems] = useState([]);
  const [paused, setPaused] = useState(false);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (filter.trim()) params.set("flight_id", filter.trim().toUpperCase());
      const { data } = await api.get(`/flights/fetch-log?${params.toString()}`);
      setItems(data?.items || []);
    } catch {
      /* tyst */
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
    if (paused) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load, paused]);

  return (
    <div
      className="card-soft p-3 space-y-2"
      data-testid="flight-fetch-log"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Plane size={14} className="text-[color:var(--moss)]" />
          <h3 className="text-sm font-semibold">Datalogg — flygdata</h3>
          {loading && <RefreshCw size={12} className="animate-spin text-[color:var(--text-tertiary)]" />}
        </div>
        <div className="flex items-center gap-1">
          <input
            type="text"
            placeholder="Filtrera (t.ex. SK1810)"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-xs h-7 px-2 rounded-md border border-[color:var(--border-subtle)] bg-white w-32"
            data-testid="fetch-log-filter"
          />
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2"
            onClick={() => setPaused((p) => !p)}
            data-testid="fetch-log-pause"
            title={paused ? "Återuppta polling" : "Pausa polling"}
          >
            {paused ? <Play size={12} /> : <Pause size={12} />}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2"
            onClick={load}
            data-testid="fetch-log-refresh"
            title="Hämta direkt"
          >
            <RefreshCw size={12} />
          </Button>
        </div>
      </div>
      <p className="text-[10px] text-[color:var(--text-tertiary)]">
        Senaste 100 anrop / events. Polas var 5:e sek. TTL: 7 dagar.
      </p>
      <div
        className="max-h-[420px] overflow-y-auto rounded border border-[color:var(--border-subtle)] bg-white"
        data-testid="fetch-log-list"
      >
        {items.length === 0 ? (
          <div className="p-6 text-center text-xs text-[color:var(--text-tertiary)]">
            <Trash2 size={14} className="inline mr-1" />
            Inga loggar ännu — vänta tills nästa polling-tick eller registrera en bevakning.
          </div>
        ) : (
          <ul className="divide-y divide-[color:var(--border-subtle)]">
            {items.map((it, idx) => (
              <li
                key={`${it.ts}-${idx}`}
                className="px-2 py-1.5 text-[11px] font-mono hover:bg-gray-50"
                data-testid={`fetch-log-item-${idx}`}
              >
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-[color:var(--text-tertiary)] tabular-nums">
                    {fmtTime(it.ts)}
                  </span>
                  <span
                    className={`px-1.5 py-px rounded border text-[10px] font-semibold ${SOURCE_COLORS[it.source] || "text-gray-700 bg-gray-50 border-gray-200"}`}
                  >
                    {it.source}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-secondary)]">
                    {it.action}
                  </span>
                  {it.flight_id && (
                    <span className="text-[10px] font-semibold text-[color:var(--moss)]">
                      {it.flight_id}
                    </span>
                  )}
                  <span className={`text-[10px] font-semibold ${STATUS_COLOR(it.status)}`}>
                    {it.status}
                  </span>
                  {it.duration_ms != null && (
                    <span className="text-[10px] text-[color:var(--text-tertiary)]">
                      {it.duration_ms}ms
                    </span>
                  )}
                </div>
                {it.summary && (
                  <div className="text-[10px] text-[color:var(--text-secondary)] mt-0.5 break-words">
                    {it.summary}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
